import { useEffect, useState } from 'react';
import { Box, Button, Menu, MenuItem, Typography } from '@mui/material';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useSnackbar } from 'notistack';
import http from '../../utils/http';

function supportsPush() {
  return window.isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function decodeKey(value) {
  const padding = '='.repeat((4 - value.length % 4) % 4);
  const binary = atob((value + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

function hasCurrentKey(subscription, publicKey) {
  const savedKey = subscription.options?.applicationServerKey;
  if (!savedKey) return false;
  const current = new Uint8Array(savedKey);
  const expected = decodeKey(publicKey);
  return current.length === expected.length && current.every((byte, index) => byte === expected[index]);
}

async function saveCurrentSubscription(registration, publicKey, createIfMissing) {
  let subscription = await registration.pushManager.getSubscription();
  if (subscription && !hasCurrentKey(subscription, publicKey)) {
    // A restored database can contain a new VAPID pair while Edge keeps the old subscription.
    try {
      await http.post('/preparation-push/unsubscribe', { endpoint: subscription.endpoint });
    } catch (error) {
      console.warn('Не удалось удалить старую push-подписку на сервере', error);
    }
    if (!await subscription.unsubscribe()) throw new Error('Could not replace the old push subscription');
    subscription = null;
    createIfMissing = true;
  }
  if (!subscription && createIfMissing) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: decodeKey(publicKey),
    });
  }
  if (subscription) {
    await http.post('/preparation-push/subscription', { subscription: subscription.toJSON() });
  }
  return subscription;
}

export default function PushNotificationControl() {
  const { enqueueSnackbar } = useSnackbar();
  const [supported] = useState(supportsPush);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(supported);
  const [permission, setPermission] = useState(() => window.Notification?.permission);
  const [menuAnchor, setMenuAnchor] = useState(null);

  useEffect(() => {
    if (!supported) return;
    let active = true;
    (async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        const existing = await registration?.pushManager.getSubscription();
        if (!existing) return;
        const { data } = await http.get('/preparation-push/config');
        const subscription = await saveCurrentSubscription(registration, data.publicKey, false);
        if (active) setSubscribed(!!subscription);
      } catch (error) {
        console.error('Не удалось проверить push-подписку', error);
        if (active) setSubscribed(false);
      } finally {
        if (active) setChecking(false);
      }
    })();
    return () => { active = false; };
  }, [supported]);

  async function enable() {
    setBusy(true);
    try {
      const { data } = await http.get('/preparation-push/config');
      const nextPermission = await Notification.requestPermission();
      setPermission(nextPermission);
      if (nextPermission !== 'granted') {
        enqueueSnackbar('Разрешите уведомления для CRM в браузере', { variant: 'warning' });
        return;
      }
      await navigator.serviceWorker.register('/push-sw.js');
      const registration = await navigator.serviceWorker.ready;
      await saveCurrentSubscription(registration, data.publicKey, true);
      setSubscribed(true);
      enqueueSnackbar('Уведомления о заявках и возвратах включены', { variant: 'success' });
    } catch (error) {
      console.error('Не удалось включить уведомления', error);
      enqueueSnackbar('Не удалось включить уведомления', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    setBusy(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        await http.post('/preparation-push/unsubscribe', { endpoint: subscription.endpoint });
        await subscription.unsubscribe();
      }
      setSubscribed(false);
      enqueueSnackbar('Уведомления выключены', { variant: 'success' });
    } catch (error) {
      console.error('Не удалось выключить уведомления', error);
      enqueueSnackbar('Не удалось выключить уведомления', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function test() {
    setBusy(true);
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const { data } = await http.get('/preparation-push/config');
      const subscription = registration && await saveCurrentSubscription(registration, data.publicKey, false);
      if (!subscription) throw new Error('Push subscription missing');
      await http.post('/preparation-push/test', { endpoint: subscription.endpoint });
      enqueueSnackbar('Проверочное уведомление отправлено', { variant: 'success' });
    } catch (error) {
      console.error('Проверка уведомлений не удалась', error);
      enqueueSnackbar('Сервер не смог отправить уведомление. Проверьте журнал Strapi.', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button
        color="inherit"
        variant="outlined"
        startIcon={subscribed ? <NotificationsActiveOutlinedIcon color="success" /> : <NotificationsOffOutlinedIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        onClick={(event) => setMenuAnchor(event.currentTarget)}
        aria-label="Уведомления"
        aria-haspopup="menu"
        aria-expanded={Boolean(menuAnchor)}
        sx={{ borderRadius: 1.5 }}
      >
        Уведомления
      </Button>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ paper: { sx: { width: 290, maxWidth: 'calc(100vw - 24px)', mt: 0.5 } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {checking ? 'Проверяем подписку…' : subscribed ? 'Уведомления включены' : 'Уведомления выключены'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {subscribed
              ? 'Новые заявки и возвраты появятся на этом устройстве.'
              : !supported
                ? 'Откройте CRM по HTTPS в браузере с поддержкой push.'
                : permission === 'denied'
                  ? 'Разрешите уведомления в настройках сайта.'
                  : 'Включите, чтобы получать новые заявки и возвраты.'}
          </Typography>
        </Box>
        <MenuItem
          onClick={() => { setMenuAnchor(null); void (subscribed ? disable() : enable()); }}
          disabled={!supported || busy || checking || (!subscribed && permission === 'denied')}
        >
          {subscribed ? 'Выключить' : 'Включить'}
        </MenuItem>
        {subscribed && (
          <MenuItem onClick={() => { setMenuAnchor(null); void test(); }} disabled={busy || checking}>
            Проверить доставку
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
